<script lang="ts">
	import { page } from '$app/stores';
	import { useLogoutMutation } from '$lib/api-v2/loginQuery';
	import { cn } from '$lib/utils/classnames';
	import { generateAvatar } from '$lib/utils/generateAvatar';
	import { fetchLicensorData } from '$lib/utils/getHeaderLicensorData';
	import { ApiUsers } from '@cf/api-v2-types';
	import { HeaderTabEnum, Image, OutboundLink } from '@cf/ui-lib';
	import { Default, MdKeyboardArrowDown, Setting } from '@cf/ui-lib/icons';
	import { clickOutside } from '@cf/ui-lib/utils';
	import { onMount } from 'svelte';
	import CounterFindLogo from '../../../assets/svg/CounterFindLogo.svelte';
	import listingsFilterSlice, {
		clearListingMetadata,
		removeAllListingsFilter
	} from '../../../routes/context/slices/listings.slice';

	export let userInfo = {} as any;

	// Update the computed property to include both ROOT and SUPER roles
	$: isAdminUser = userInfo?.role === 'ROOT' || userInfo?.role === 'SUPER';

	const logoutMutation = useLogoutMutation();
	const apiUsers = new ApiUsers('/proxy');

	let isProfileOpen = false;
	let isSettingOpen = false;
	let active = '';

	let profileImg =
		userInfo?.avatar ||
		'https://fastly.picsum.photos/id/168/536/354.jpg?hmac=YVT9XyqbWBrJxY74bob-kGl5Q448lpGP3ZDtDIfV3lU';

	$: {
		active = getActiveTabFromPath($page.url.pathname)?.toString();
	}

	let markForReviewCount = 0;
	let licensors: any[] = [];
	let error: string | null = null;
	let selectedLicensor: any = null;
	let userLicensors: any[] = [];

	$: currentSubdomain = $page.url.hostname.split('.')[0];

	// Fetch user licensors when userInfo changes
	$: {
		if (userInfo?.id) {
			fetchUserLicensors();
		}
	}

	async function fetchUserLicensors() {
		try {
			const response = await apiUsers.getUserLicensors(userInfo.id);
			userLicensors = [
				...(response.primary_licensor ? [response.primary_licensor] : []),
				...response.assigned_licensors
			];
		} catch (error) {
			console.error('Error fetching user licensors:', error);
		}
	}

	onMount(async () => {
		const data = await fetchLicensorData(currentSubdomain);
		markForReviewCount = data.markForReviewCount;
		licensors = data.licensors;
		selectedLicensor = data.selectedLicensor;
		error = data.error;
	});

	let isLicensorDropdownOpen = false;
	let searchTerm = '';
	let filteredLicensors: any[] = [];

	$: {
		// For ROOT/SUPER users, show all licensors
		if (isAdminUser) {
			filteredLicensors = licensors.filter((licensor) =>
				licensor.displayName.toLowerCase().includes(searchTerm.toLowerCase())
			);
		} else {
			// For other users, only show accessible licensors
			const accessibleLicensors = licensors.filter((licensor) =>
				userLicensors.some((userLicensor) => userLicensor.id === licensor.id)
			);
			filteredLicensors = accessibleLicensors.filter((licensor) =>
				licensor.displayName.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}
	}

	// Show dropdown for ROOT/SUPER users or users with multiple licensors
	$: showLicensorDropdown = isAdminUser || userLicensors.length > 1;

	function handleSearch(event: Event) {
		searchTerm = (event.target as HTMLInputElement).value;
	}

	function toggleLicensorDropdown() {
		isLicensorDropdownOpen = !isLicensorDropdownOpen;
	}

	function selectLicensor(licensor: any) {
		selectedLicensor = licensor;
		isLicensorDropdownOpen = false;
		if (licensor && licensor.subdomain) {
			const currentUrl = $page.url;
			const protocol = currentUrl.protocol;
			const hostParts = currentUrl.hostname.split('.');
			const domain = hostParts.slice(1).join('.');
			const port = currentUrl.port ? `:${currentUrl.port}` : '';
			const newUrl = `${protocol}//${licensor.subdomain}.${domain}${port}/uilib/listings`;
			window.location.href = newUrl;
		} else {
			console.error('Invalid licensor or missing subdomain');
		}
	}

	function handleImageError(event: Event) {
		const img = event.target as HTMLImageElement;
		img.src = Default;
	}

	const adminMenu = () => {
		isSettingOpen = !isSettingOpen;
		isProfileOpen = false;
	};

	const userProfile = () => {
		isProfileOpen = !isProfileOpen;
		isSettingOpen = false;
	};

	const getActiveTabFromPath = (path: string) => {
		if (!path.includes('listings')) {
			const currentFilterSlice = $listingsFilterSlice;
			if (currentFilterSlice) {
				$listingsFilterSlice = [];
				removeAllListingsFilter();
			}
		}
		if (path.includes('overview')) {
			return HeaderTabEnum.Overview;
		} else if (path.includes('listings')) {
			return HeaderTabEnum.Listings;
		} else if (path.includes('domains')) {
			return HeaderTabEnum.Domains;
		} else if (path.includes('sellers')) {
			return HeaderTabEnum.Sellers;
		} else if (path.includes('categories') && isAdminUser) {
			return 'categories';
		} else if (path.includes('licensors') && isAdminUser) {
			return 'licensors';
		} else {
			return 0;
		}
	};

	const onLogout = async () => {
		const currentFilterSlice = $listingsFilterSlice;
		if (currentFilterSlice) {
			$listingsFilterSlice = [];
			removeAllListingsFilter();
			clearListingMetadata();
		}
		await $logoutMutation.mutateAsync();
		window.location.href = '/auth';
	};

	const handleClickOutside = (e: any) => {
		isSettingOpen = false;
		isProfileOpen = false;
	};
</script>

<header class="border-b border-b-border bg-white p-4" id="scroolHeader">
	<nav class="flex items-center justify-between">
		<div class="relative flex items-center gap-10">
			<div class="flex items-center space-x-4">
				<CounterFindLogo />
			</div>
			<!-- New Licensor Dropdown (only visible for ROOT/SUPER users or users with multiple licensors) -->
			{#if showLicensorDropdown}
				<div class="relative mx-4" use:clickOutside={() => (isLicensorDropdownOpen = false)}>
					{#if error}
						<div class="text-red-500">Error: {error}</div>
					{:else if licensors.length === 0}
						<div>No licensors available</div>
					{:else}
						<button
							class="flex items-center space-x-2 bg-white px-4 py-2 text-gray-700 focus:outline-none"
							on:click={toggleLicensorDropdown}
						>
							{#if selectedLicensor}
								<div class="mr-2 flex h-6 w-8 items-center justify-center">
									<Image
										src={selectedLicensor.logo || Default}
										alt=""
										class="h-6 w-8 object-contain"
										on:error={handleImageError}
									/>
								</div>
								<span class="text-sm font-medium">{selectedLicensor.displayName}</span>
							{:else}
								<span class="text-sm font-medium">Select Licensor</span>
							{/if}
							<MdKeyboardArrowDown />
						</button>

						{#if isLicensorDropdownOpen}
							<div
								class="absolute left-0 z-50 mt-2 max-h-80 w-64 overflow-y-auto rounded-md bg-white shadow-lg"
							>
								<div class="sticky top-0 z-10 bg-white p-2">
									<input
										type="text"
										placeholder="Search licensors..."
										class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
										on:input={handleSearch}
									/>
								</div>
								{#each filteredLicensors as licensor}
									<button
										class="flex w-full items-center space-x-3 border-b border-gray-100 px-4 py-3 text-left text-gray-700 last:border-b-0 hover:bg-gray-100"
										on:click={() => selectLicensor(licensor)}
									>
										<div class="flex h-8 w-10 flex-shrink-0 items-center justify-center">
											<Image
												src={licensor.logo || Default}
												alt=""
												class="h-8 w-10 object-contain"
												on:error={handleImageError}
											/>
										</div>
										<span class="truncate text-sm">{licensor.displayName}</span>
									</button>
								{/each}
							</div>
						{/if}
					{/if}
				</div>
			{/if}
			<div class="space-x-4">
				<OutboundLink
					class={cn({
						'px-3 py-2 font-medium text-neutral-800 transition-all duration-300 ease-in-out hover:text-primary': true,
						'text-primary': active === HeaderTabEnum.Overview
					})}
					href="/uilib/overview">Overview</OutboundLink
				>
				<OutboundLink
					class={cn({
						'px-3 py-2 font-medium text-neutral-800 transition-all duration-300 ease-in-out hover:text-primary': true,
						'text-primary': active === HeaderTabEnum.Listings
					})}
					href="/uilib/listings">Listings</OutboundLink
				>

				<OutboundLink
					class={cn({
						'px-3 py-2 font-medium text-neutral-800 transition-all duration-300 ease-in-out hover:text-primary': true,
						'text-primary': active === HeaderTabEnum.Domains
					})}
					href="/uilib/domains">Domains</OutboundLink
				>

				<OutboundLink
					class={cn({
						'px-3 py-2 font-medium text-neutral-800 transition-all duration-300 ease-in-out hover:text-primary': true,
						'text-primary': active === HeaderTabEnum.Sellers
					})}
					href="/uilib/sellers/list">Sellers</OutboundLink
				>

				<!-- Review Tab (only visible for ROOT and SUPER users) -->
				{#if isAdminUser}
					<OutboundLink
						class={cn({
							'px-3 py-2 font-medium text-neutral-800 transition-all duration-300 ease-in-out hover:text-primary': true,
							'text-primary': active === 'licensors'
						})}
						href="/uilib/licensors"
					>
						Review
						{#if markForReviewCount > 0}
							<span
								class="custom-red-bg ml-1 inline-flex items-center justify-center rounded-md px-2 py-0.5 text-xs font-bold text-white"
							>
								{markForReviewCount}
							</span>
						{/if}
					</OutboundLink>
				{/if}
			</div>
		</div>
		<div class="flex items-center gap-5" use:clickOutside={handleClickOutside}>
			<button type="button" class="relative cursor-pointer" on:click={() => adminMenu()}>
				<Setting />
				<div
					class={cn({
						'absolute left-0 top-full z-20 mt-3 w-44 rounded-md bg-white px-4 py-2 shadow-[0px_1px_10px_0px_#00000030]': true,
						block: isSettingOpen,
						hidden: !isSettingOpen
					})}
				>
					<ul class="relative h-full">
						{#if isAdminUser}
							<li class="mb-2 text-left">
								<OutboundLink
									class={`relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full ${active === HeaderTabEnum.Overview ? ' text-primary  ' : ''}`}
									href="/uilib/overview">Overview</OutboundLink
								>
							</li>
							<li class="mb-2 text-left">
								<OutboundLink
									class={`relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full ${$page.url.pathname === '/uilib/licensors/licensors-view' ? ' text-primary ' : ''}`}
									href="/uilib/licensors/licensors-view">Licensors</OutboundLink
								>
							</li>
							<li class="mb-2 text-left">
								<OutboundLink
									class={`relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full ${$page.url.pathname === '/uilib/categories' ? ' text-primary ' : ''}`}
									href="/uilib/categories">Categories</OutboundLink
								>
							</li>
							<li class="mb-2 text-left">
								<OutboundLink
									class={`relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full ${$page.url.pathname === '/uilib/marketplaces' ? ' text-primary ' : ''}`}
									href="/uilib/marketplaces">Marketplaces</OutboundLink>
							</li>
						{/if}
						<li class="mb-2 text-left">
							<OutboundLink
								class={`relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full ${$page.url.pathname === '/uilib/brands/brands-view' ? ' text-primary ' : ''}`}
								href="/uilib/brands/brands-view">Brands</OutboundLink
							>
						</li>
						<!-- <li class="mb-2 text-left">
							<OutboundLink
								class={`relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-[3px] after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full ${$page.url.pathname === '/uilib/roles_permissions' ? ' text-primary  ' : ''}`}
								href="/roles_permissions">Roles & Permissions</OutboundLink
							>
						</li> -->
						{#if isAdminUser}
							<li class="mb-2 text-left">
								<OutboundLink
									class="relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full"
									href="/uilib/users">Users</OutboundLink
								>
							</li>
							<li class="mb-2 text-left">
								<OutboundLink
									class="relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full"
									href="/uilib/sdc-settings">SDC Settings</OutboundLink
								>
							</li>
							<li class="mb-2 text-left">
								<OutboundLink
									class="relative font-inter text-sm font-medium leading-5 text-dark after:absolute after:left-0 after:top-full after:h-0.5 after:w-0 after:bg-primary after:transition-all after:duration-200 after:ease-in-out hover:text-primary hover:after:w-full"
									href="/uilib/listings-scanner">Listings Scanner</OutboundLink
								>
							</li>
						{/if}
					</ul>
				</div>
			</button>
			<button
				type="button"
				class="relative flex cursor-pointer items-center gap-2.5"
				on:click={() => userProfile()}
			>
				<img
					src={profileImg}
					alt=""
					class="h-[30px] w-[30px] rounded-full object-cover object-center"
					use:generateAvatar={`${userInfo?.first_name?.[0] || ''} ${userInfo?.last_name?.[0] || ''}`}
				/>
				<span
					class="flex items-center gap-2.5 whitespace-nowrap font-inter text-sm font-semibold leading-[21px] text-dark"
				>
					{`${userInfo?.first_name} ${userInfo?.last_name}`}
					<MdKeyboardArrowDown />
				</span>
				<div
					class={cn({
						'absolute left-0 top-full z-10 mt-2 rounded-md bg-white px-4 py-2 shadow-[0px_1px_10px_0px_#00000030]': true,
						block: isProfileOpen,
						hidden: !isProfileOpen
					})}
				>
					<ul>
						<!-- <li class="mb-2 text-left">
							<OutboundLink
								class="font-inter text-sm font-medium leading-5 text-dark"
								href="/profile"
								target="_self">User Profile</OutboundLink
							>
						</li> -->
						<li class="text-left">
							<button class="font-inter text-sm font-medium leading-5 text-dark" on:click={onLogout}
								>Log out</button
							>
						</li>
					</ul>
				</div>
			</button>
		</div>
	</nav>
</header>

<style lang="postcss">
	.custom-red-bg {
		@apply bg-[#ee6b6e];
	}
</style>
